/**
 * Review and submission helpers for the canonical form system.
 *
 * These functions handle the "last mile" of form data:
 * - formatting values for human-readable review screens
 * - shaping payloads for API submission
 * - cleaning up empty/stale values
 *
 * Pages should use these helpers instead of writing inline formatting
 * logic. If a formatting case is missing, add it here - not in the page.
 */

import type { ReactNode } from 'react'

import type { FormFieldMeta, SelectOption } from './types'
import { FILE_FIELD_TYPES, inferReviewFormat } from './types'
import { formatPhoneDisplay } from './phone'
import { formatDisplayValue } from './display'
import { addressDisplay } from '@/shared/lib/formatting/address'
import { warnMissingCurrency } from '@/shared/lib/formatting/dev-warn'
import { titleCaseKey } from '@/shared/lib/humanise'

// ── Format options ──────────────────────────────────────────

/**
 * Locale / currency context for review formatting.
 *
 * In admin pages, pass these from `useFormatting()`:
 *   const { locale, currency } = useFormatting()
 *   formatReviewValue(field, value, { locale, currency })
 *
 * Defaults to 'en-GB' / 'GBP' when omitted.
 */
export interface FormatOptions {
  /** BCP 47 locale tag (e.g. 'en-GB', 'en-US', 'de-DE'). */
  locale?: string
  /** ISO 4217 currency code (e.g. 'GBP', 'USD', 'EUR'). */
  currency?: string
}

const DEFAULT_LOCALE = 'en-GB'
const DEFAULT_CURRENCY = 'GBP'

// ── Review formatting ───────────────────────────────────────

export interface ReviewField {
  label: string
  value: string
  codeName: string
  isEmpty: boolean
}

/**
 * Format a single field value for display in a review screen.
 *
 * Pass `formatOptions` with `locale` and `currency` from org preferences
 * so dates, numbers, and currencies render in the tenant's format.
 *
 * Returns a human-readable string. Never returns raw objects or arrays.
 */
export function formatReviewValue(
  field: FormFieldMeta,
  value: unknown,
  formatOptions?: FormatOptions,
): string {
  if (value === undefined || value === null || value === '') {
    return '-'
  }

  const locale = formatOptions?.locale ?? DEFAULT_LOCALE
  const currency = formatOptions?.currency ?? DEFAULT_CURRENCY
  const format = inferReviewFormat(field)

  switch (format) {
    case 'boolean':
      return value === true || value === 'true' ? 'Yes' : 'No'

    case 'date':
      return formatDate(value, locale)

    case 'datetime':
      return formatDateTime(value, locale)

    case 'currency': {
      if (formatOptions?.currency === undefined) {
        warnMissingCurrency('formatReviewValue', field.code_name)
      }
      // MONEY / CURRENCY persists as `{amount, currency}`. The CurrencyInput
      // persists a default `currency` on mount, so an untouched field arrives
      // as `{currency: 'GBP'}` with no `amount` - treat that as empty rather
      // than stringifying the object into "[object Object]".
      if (typeof value === 'object' && value !== null) {
        const obj = value as { amount?: unknown; currency?: string }
        if (obj.amount === undefined || obj.amount === null || obj.amount === '') {
          return '-'
        }
        // The org/tenant currency (`currency`) is authoritative; the currency
        // baked into the stored value is ignored so money follows the tenant's
        // currency choice.
        return formatCurrency(obj.amount, currency, locale)
      }
      return formatCurrency(value, currency, locale)
    }

    case 'number':
      return formatNumber(value, locale)

    case 'select':
      return resolveOptionLabel(value as string, field.options)

    case 'multi_select': {
      if (!Array.isArray(value)) return String(value)
      return value
        .map((v) => resolveOptionLabel(v, field.options))
        .join(', ') || '-'
    }

    case 'file':
    case 'file_count':
      return formatFileValue(value)

    case 'phone':
      return formatPhoneDisplay(String(value)) || '-'

    case 'entity_search': {
      // A picked resource reference (e.g. dealer/retailer) persists as a
      // composite object like `{ uuid, name }`. Render its label instead of
      // stringifying it into "[object Object]".
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        const obj = value as Record<string, unknown>
        const label = [obj.display, obj.name, obj.label].find(
          (v): v is string => typeof v === 'string' && v.trim().length > 0,
        )
        if (label) return label
        const dunder = Object.entries(obj).find(
          ([k, v]) => k.includes('__') && typeof v === 'string' && v.trim().length > 0,
        )
        if (dunder) return dunder[1] as string
        return '-'
      }
      return value ? String(value) : '-'
    }

    case 'address':
      // ADDRESS persists as a composite object; render the canonical single-line
      // address instead of stringifying it into "[object Object]".
      return addressDisplay(value) || '-'

    default:
      return String(value)
  }
}

/**
 * Build an array of review fields from form fields and values.
 *
 * Filters out hidden fields and optionally empty values.
 */
export function buildReviewFields(
  fields: FormFieldMeta[],
  values: Record<string, unknown>,
  options?: {
    /** Include fields with empty values. Defaults to true. */
    includeEmpty?: boolean
    /** Only include fields in these groups. */
    groups?: string[]
    /** Exclude fields with these code names. */
    excludeCodes?: Set<string>
    /** Locale / currency for value formatting. */
    format?: FormatOptions
  },
): ReviewField[] {
  const {
    includeEmpty = true,
    groups,
    excludeCodes,
    format,
  } = options ?? {}

  return fields
    .filter((f) => !f.hidden)
    .filter((f) => !excludeCodes || !excludeCodes.has(f.code_name))
    .filter((f) => !groups || (f.group && groups.includes(f.group)))
    .map((field) => {
      const raw = values[field.code_name]
      const formatted = formatReviewValue(field, raw, format)
      const isEmpty = raw === undefined || raw === null || raw === '' ||
        (Array.isArray(raw) && raw.length === 0)

      return {
        label: field.display_name,
        value: formatted,
        codeName: field.code_name,
        isEmpty,
      }
    })
    .filter((rf) => includeEmpty || !rf.isEmpty)
}

// ── Payload shaping ─────────────────────────────────────────

/**
 * Build a clean submission payload from form values.
 *
 * - Strips empty strings (keeps `false` for booleans, empty arrays for multi-select)
 * - Strips values for hidden or disabled fields (optional)
 * - Strips file field values (they're submitted via separate upload endpoints)
 *
 * Returns a clean `Record<string, unknown>` ready for API submission.
 */
export function buildPayload(
  fields: FormFieldMeta[],
  values: Record<string, unknown>,
  options?: {
    /** Strip empty string values. Defaults to true. */
    stripEmpty?: boolean
    /** Strip values for disabled fields. Defaults to false. */
    stripDisabled?: boolean
    /** Strip file field values (handled by upload). Defaults to false. */
    stripFiles?: boolean
    /** Only include fields in these groups. */
    groups?: string[]
    /** Extra values to merge into the payload. */
    extraValues?: Record<string, unknown>
  },
): Record<string, unknown> {
  const {
    stripEmpty = true,
    stripDisabled = false,
    stripFiles = false,
    groups,
    extraValues,
  } = options ?? {}

  const payload: Record<string, unknown> = {}

  for (const field of fields) {
    if (field.hidden) continue
    if (stripDisabled && field.disabled) continue
    if (groups && (!field.group || !groups.includes(field.group))) continue

    const ft = field.field_type.toUpperCase()

    if (stripFiles && FILE_FIELD_TYPES.has(ft)) continue

    const val = values[field.code_name]

    if (ft === 'BOOLEAN') {
      payload[field.code_name] = val ?? false
      continue
    }

    if (ft === 'MULTI_SELECT') {
      payload[field.code_name] = Array.isArray(val) ? val : []
      continue
    }

    if (stripEmpty && (val === '' || val === undefined || val === null)) {
      continue
    }

    payload[field.code_name] = val
  }

  if (extraValues) {
    Object.assign(payload, extraValues)
  }

  return payload
}

// ── Helpers ─────────────────────────────────────────────────

function formatDate(value: unknown, locale: string): string {
  if (!value) return '-'
  try {
    const iso = String(value)
    const dateStr = iso.includes('T') ? iso : iso + 'T12:00:00'
    const d = new Date(dateStr)
    if (isNaN(d.getTime())) return String(value)
    return new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(d)
  } catch {
    return String(value)
  }
}

function formatDateTime(value: unknown, locale: string): string {
  if (!value) return '-'
  try {
    const d = new Date(String(value))
    if (isNaN(d.getTime())) return String(value)
    return new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(d)
  } catch {
    return String(value)
  }
}

function formatCurrency(value: unknown, currencyCode: string, locale: string): string {
  const num = typeof value === 'number' ? value : Number(value)
  if (isNaN(num)) return String(value)
  try {
    const hasDecimals = num % 1 !== 0
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: hasDecimals ? 2 : 0,
      maximumFractionDigits: hasDecimals ? 2 : 0,
    }).format(num)
  } catch {
    return `${currencyCode} ${num}`
  }
}

function formatNumber(value: unknown, locale: string): string {
  const num = typeof value === 'number' ? value : Number(value)
  if (isNaN(num)) return String(value)
  try {
    return new Intl.NumberFormat(locale).format(num)
  } catch {
    return String(num)
  }
}

function resolveOptionLabel(
  value: string,
  options?: SelectOption[],
): string {
  if (!options) return titleCaseKey(value)
  const match = options.find((o) => o.value === value)
  return match?.label || titleCaseKey(value)
}


function formatFileValue(value: unknown): string {
  if (typeof value === 'string') {
    return value || '-'
  }

  if (Array.isArray(value)) {
    const count = value.length
    if (count === 0) return '-'
    return `${count} file${count === 1 ? '' : 's'}`
  }

  if (typeof value === 'object' && value !== null) {
    const obj = value as Record<string, unknown>
    if (obj.display && typeof obj.display === 'string') return obj.display
    if (obj.file_name) return String(obj.file_name)
    const nested = obj.value as Record<string, unknown> | undefined
    if (nested?.file_name) return String(nested.file_name)
    if (obj.uuid) return 'Uploaded'
    return '-'
  }

  return '-'
}

/**
 * ReactNode variant of {@link formatReviewValue} for wizard review steps
 * that have access to the rich upload envelopes captured at submit time.
 *
 * File/photo fields are rendered as an underlined filename link (via
 * {@link formatDisplayValue}) instead of the bare UUID string that the
 * submission payload carries. `uploads` is the rich
 * `{ code_name -> { uuid, file_name, file_url } }` map produced by
 * `useForm` (the submission `value` stays a bare UUID). All other field
 * types fall back to the plain-text {@link formatReviewValue}.
 */
export function formatReviewValueNode(
  field: FormFieldMeta,
  value: unknown,
  uploads?: Record<string, unknown>,
  formatOptions?: FormatOptions,
): ReactNode {
  if (FILE_FIELD_TYPES.has(field.field_type.toUpperCase())) {
    const rich = uploads?.[field.code_name] ?? value
    return formatDisplayValue(field, rich, formatOptions)
  }
  return formatReviewValue(field, value, formatOptions)
}
